import React from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { canAccessModule } from "../../lib/rbac";
import {
  LayoutDashboard,
  Users,
  Camera,
  MapPin,
  Stethoscope,
  Radio,
  Bell,
  ScanFace,
  CreditCard,
  Receipt,
  Layers,
  Package,
  Headphones,
  ShieldCheck,
  History,
  Megaphone,
  TrendingUp,
  Lock,
  GitPullRequest,
  Cloud,
  PanelLeftClose,
  PanelLeftOpen,
  Sparkles,
  ShoppingCart,
} from "lucide-react";

export const Sidebar: React.FC<{ collapsed?: boolean; onToggle?: () => void }> = ({
  collapsed = false,
  onToggle,
}) => {
  const { role } = useAuth();

  const toggleBtn = onToggle && (
    <button
      onClick={onToggle}
      title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: "28px", height: "28px", borderRadius: "6px", cursor: "pointer",
        border: "1px solid var(--border-dim)", background: "transparent", color: "var(--text-muted)",
      }}
    >
      {collapsed ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />}
    </button>
  );

  return (
    <aside className="admin-sidebar">
      {/* Brand & Platform Identity */}
      <div
        style={{
          padding: collapsed ? "16px 0 12px" : "20px 22px 14px",
          borderBottom: "1px solid var(--border-subtle)",
        }}
      >
        <div
          style={{
            display: "flex", alignItems: "center", gap: "10px",
            justifyContent: collapsed ? "center" : "space-between",
            flexDirection: collapsed ? "column" : "row",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
            {/* MWM eye mark — matches the app icon + favicon. */}
            <svg
              width="32"
              height="32"
              viewBox="0 0 100 100"
              aria-label="My-Watchman"
              style={{ flexShrink: 0, boxShadow: "0 2px 8px rgba(2, 132, 199, 0.4)", borderRadius: "8px" }}
            >
              <rect width="100" height="100" rx="20" fill="#0A0A0A" />
              <g fill="none" stroke="#9AA0A6" strokeWidth="7" strokeLinecap="round">
                <path d="M18 51 Q50 24 82 51" />
                <path d="M18 57 Q50 84 82 57" />
              </g>
              <circle cx="50" cy="54" r="19" fill="#12A0F0" />
              <circle cx="47" cy="56" r="8.6" fill="#0A0A0A" />
              <circle cx="43.4" cy="52.4" r="4.2" fill="#12A0F0" />
            </svg>
            {!collapsed && (
              <div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "var(--text-main)",
                    letterSpacing: "-0.02em",
                  }}
                >
                  My-Watchman™
                </div>
                <div
                  style={{
                    fontSize: "10.5px",
                    color: "var(--text-dim)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Platform Ops Console
                </div>
              </div>
            )}
          </div>
          {toggleBtn}
        </div>
      </div>

      {/* Navigation Groups */}
      <div style={{ padding: "10px 0 24px", flex: 1 }}>
        {/* Home */}
        {canAccessModule(role, "overview") && (
          <NavLink to="/" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} end>
            <LayoutDashboard size={16} />
            <span>Overview</span>
          </NavLink>
        )}

        {/* Device Management — the cameras and hardware themselves. */}
        {(canAccessModule(role, "cameras") || canAccessModule(role, "inventory")) && (
          <>
            <div className="nav-group-title">Device Management</div>
            {canAccessModule(role, "cameras") && (
              <NavLink to="/cameras" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <Camera size={16} /><span>All Cameras</span>
              </NavLink>
            )}
            {canAccessModule(role, "camera-versions") && (
              <NavLink to="/camera-versions" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <Layers size={16} /><span>Camera Versions</span>
              </NavLink>
            )}
            {canAccessModule(role, "camera-map") && (
              <NavLink to="/camera-map" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <MapPin size={16} /><span>Camera Map</span>
              </NavLink>
            )}
            {canAccessModule(role, "inventory") && (
              <NavLink to="/inventory" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <Package size={16} /><span>Camera Models & Stock</span>
              </NavLink>
            )}
          </>
        )}

        {/* Fleet Operations — running & monitoring the live fleet. */}
        {(canAccessModule(role, "customer-fleets") || canAccessModule(role, "cloud-storage") || canAccessModule(role, "alerts")) && (
          <>
            <div className="nav-group-title">Fleet Operations</div>
            {canAccessModule(role, "customer-fleets") && (
              <NavLink to="/customer-fleets" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <Users size={16} /><span>Customer Fleets</span>
              </NavLink>
            )}
            {canAccessModule(role, "offline-triage") && (
              <NavLink to="/offline-triage" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <GitPullRequest size={16} /><span>Offline Triage</span>
              </NavLink>
            )}
            {canAccessModule(role, "cloud-storage") && (
              <NavLink to="/cloud-storage" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <Cloud size={16} /><span>Cloud Storage</span>
              </NavLink>
            )}
            {canAccessModule(role, "sim-management") && (
              <NavLink to="/sim-management" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <Radio size={16} /><span>SIM / 4G Fleet</span>
              </NavLink>
            )}
            {canAccessModule(role, "alerts") && (
              <NavLink to="/alerts" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <Bell size={16} /><span>Alerts Stream</span>
              </NavLink>
            )}
            {canAccessModule(role, "alerts") && (
              <NavLink to="/faces" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <ScanFace size={16} /><span>Faces</span>
              </NavLink>
            )}
          </>
        )}

        {/* Customer Management — customer accounts. */}
        {canAccessModule(role, "customers") && (
          <>
            <div className="nav-group-title">Customer Management</div>
            <NavLink to="/customers" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              <Users size={16} /><span>Customers & 360</span>
            </NavLink>
          </>
        )}

        {/* Billing & Transactions — money in and out. */}
        {(canAccessModule(role, "subscriptions") || canAccessModule(role, "analytics")) && (
          <>
            <div className="nav-group-title">Billing &amp; Transactions</div>
            {canAccessModule(role, "subscriptions") && (
              <NavLink to="/subscriptions" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <CreditCard size={16} /><span>Subscriptions & Comp</span>
              </NavLink>
            )}
            {canAccessModule(role, "payments") && (
              <NavLink to="/payments" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <Receipt size={16} /><span>Payment Ledger</span>
              </NavLink>
            )}
            {canAccessModule(role, "inventory") && (
              <NavLink to="/orders" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <ShoppingCart size={16} /><span>Camera Orders</span>
              </NavLink>
            )}
            {canAccessModule(role, "plans") && (
              <NavLink to="/plans" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <Layers size={16} /><span>Plans Catalogue</span>
              </NavLink>
            )}
            {canAccessModule(role, "analytics") && (
              <NavLink to="/analytics" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <TrendingUp size={16} /><span>Revenue Analytics</span>
              </NavLink>
            )}
          </>
        )}

        {/* Support — SOS/emergencies is owned by the separate SOS platform. */}
        {canAccessModule(role, "tickets") && (
          <>
            <div className="nav-group-title">Support</div>
            <NavLink to="/tickets" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              <Headphones size={16} /><span>Support Tickets</span>
            </NavLink>
          </>
        )}

        {/* Platform Admin — staff, audit, announcements, AI cost. */}
        {(canAccessModule(role, "admins") || canAccessModule(role, "audit-log") || canAccessModule(role, "broadcast")) && (
          <>
            <div className="nav-group-title">Platform Admin</div>
            {canAccessModule(role, "admins") && (
              <NavLink to="/admins" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <ShieldCheck size={16} /><span>Admins & Roles</span>
              </NavLink>
            )}
            {canAccessModule(role, "audit-log") && (
              <NavLink to="/audit-log" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <History size={16} /><span>Audit Log</span>
              </NavLink>
            )}
            {canAccessModule(role, "broadcast") && (
              <NavLink to="/broadcast" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <Megaphone size={16} /><span>Broadcast</span>
              </NavLink>
            )}
            {canAccessModule(role, "analytics") && (
              <NavLink to="/ai-usage" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                <Sparkles size={16} /><span>AI Usage</span>
              </NavLink>
            )}
          </>
        )}

        {/* GROUP 5: Account */}
        <div className="nav-group-title">Account</div>
        <NavLink
          to="/security"
          className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
        >
          <Lock size={16} />
          <span>Security & 2FA</span>
        </NavLink>
      </div>
    </aside>
  );
};
